import { useEffect, useState } from 'react';
import { RotateCcw, X, Globe, User as UserIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { reportTemplatesApi, type Template } from '../lib/reportTemplatesApi';

interface Props {
  open: boolean;
  onClose: () => void;
  onRestored: (template: Template) => void;
}

export default function DeletedTemplatesModal({ open, onClose, onRestored }: Props) {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    reportTemplatesApi
      .listDeleted()
      .then(setTemplates)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const handleRestore = async (t: Template) => {
    setRestoringId(t.id);
    try {
      const restored = await reportTemplatesApi.restore(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      onRestored(restored);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Wiederherstellen fehlgeschlagen');
    } finally {
      setRestoringId(null);
    }
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  // Gleiche Regel wie restore() im Backend — sonst zeigt der Button eine Aktion an,
  // die beim Klick nur mit einem 403 endet.
  const canRestore = (t: Template) => !!user?.isAdmin || (t.scope === 'user' && t.createdByUserId === user?.id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`rounded-xl border shadow-xl p-6 max-w-lg w-full max-h-[80vh] flex flex-col ${card}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Gelöschte Vorlagen</h2>
          <button onClick={onClose} className={muted} aria-label="Schließen">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-3 p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{error}</div>
        )}

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <p className={`text-sm ${muted}`}>Lädt…</p>
          ) : templates.length === 0 ? (
            <p className={`text-sm italic ${muted}`}>Keine gelöschten Vorlagen.</p>
          ) : (
            templates.map((t) => (
              <div
                key={t.id}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {t.scope === 'system' ? (
                    <Globe className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                  ) : (
                    <UserIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium truncate">{t.name}</span>
                </div>
                {canRestore(t) && (
                  <button
                    onClick={() => handleRestore(t)}
                    disabled={restoringId === t.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700 disabled:opacity-50 flex-shrink-0"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Wiederherstellen
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
